import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { storage } from "../firebase";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import "./AlbumDetails.css";

const AlbumDetails = () => {
  const { albumName } = useParams();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [albumTitle, setAlbumTitle] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const masonryRef = useRef(null);
  const [imageHeights, setImageHeights] = useState([]);

  useEffect(() => {
    if (!albumName) return;

    const displayName = albumName.replace(/_/g, " ").replace(/-/g, " ");
    setAlbumTitle(displayName);

    const albumRef = ref(storage, `mywork/${albumName}/`);

    listAll(albumRef)
      .then(async (res) => {
        const imageUrls = await Promise.all(
          res.items.map(async (item) => {
            const url = await getDownloadURL(item);
            const fileName = item.name.replace(/\.[^/.]+$/, "");
            const nameParts = fileName.split('_').map(part => 
              part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            );
            const imageName = nameParts.join(' ');
            
            return {
              url,
              name: imageName || displayName,
              fileName: item.name,
              fullPath: item.fullPath,
              date: "Dec.25, 2025",
              // Add random height class for masonry effect
              heightClass: getRandomHeightClass()
            };
          })
        );
        setImages(imageUrls);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading album images:", error);
        setLoading(false);
      });
  }, [albumName]);

  // Function to assign random height classes
  const getRandomHeightClass = () => {
    const heights = ['tall', 'medium', 'short'];
    return heights[Math.floor(Math.random() * heights.length)];
  };

  // Handle image load to get actual dimensions
  const handleImageLoad = (index, e) => {
    const img = e.target;
    const aspectRatio = img.naturalHeight / img.naturalWidth;
    
    // Update height class based on actual aspect ratio
    const newHeightClass = aspectRatio > 1.3 ? 'tall' : 
                           aspectRatio < 0.8 ? 'short' : 'medium';
    
    setImages(prev => prev.map((img, i) => 
      i === index ? { ...img, heightClass: newHeightClass } : img
    ));
  };

  const handleBackClick = () => {
    window.history.back();
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevImage = (e) => {
    e.stopPropagation();
    if (selectedImage) {
      const currentIndex = images.findIndex(img => img.url === selectedImage.url);
      if (currentIndex > 0) {
        setSelectedImage(images[currentIndex - 1]);
      }
    }
  };

  const goToNextImage = (e) => {
    e.stopPropagation();
    if (selectedImage) {
      const currentIndex = images.findIndex(img => img.url === selectedImage.url);
      if (currentIndex < images.length - 1) {
        setSelectedImage(images[currentIndex + 1]);
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;
      
      switch(e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          goToPrevImage({ stopPropagation: () => {} });
          break;
        case 'ArrowRight':
          goToNextImage({ stopPropagation: () => {} });
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, selectedImage, images]);

  if (loading) {
    return (
      <div className="album-details-container">
        <button className="back-button" onClick={handleBackClick}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Albums
        </button>
        
        <div className="masonry-grid-loading">
          {[...Array(12)].map((_, i) => (
            <div 
              key={i} 
              className={`masonry-item-loading ${getRandomHeightClass()}`}
            >
              <div className="masonry-image-skeleton"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="album-details-container">
      {/* Lightbox Modal */}
      {isLightboxOpen && selectedImage && (
        <div className="lightbox-overlay active" onClick={closeLightbox}>
          <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="lightbox-image-container">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.name} 
                className="lightbox-image"
              />
            </div>
            
            
            
            <div className="lightbox-nav">
              {images.findIndex(img => img.url === selectedImage.url) > 0 && (
                <button className="nav-btn prev-btn" onClick={goToPrevImage}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              {images.findIndex(img => img.url === selectedImage.url) < images.length - 1 && (
                <button className="nav-btn next-btn" onClick={goToNextImage}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <button className="back-button" onClick={handleBackClick}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Albums
      </button>

      {/* Album Header - Simplified for masonry style */}
      <div className="album-header">
        <h1 className="album-main-title">{albumTitle}</h1>
        <div className="album-meta">
          <span className="meta-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {images.length} Photographs
          </span>
        </div>
      </div>

      {/* Masonry Grid */}
      {images.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="empty-title">No Images Found</h2>
          <p className="empty-description">This album is currently empty.</p>
        </div>
      ) : (
        <div className="masonry-grid" ref={masonryRef}>
          {images.map((image, index) => (
            <div 
              key={index} 
              className={`masonry-item ${image.heightClass}`}
              onClick={() => openLightbox(image)}
            >
              <div className="masonry-image-container">
                <img
                  src={image.url}
                  alt={image.name}
                  className="masonry-image"
                  loading="lazy"
                  onLoad={(e) => handleImageLoad(index, e)}
                />
                <div className="masonry-overlay">
                  <div className="masonry-overlay-content">
                    <p className="masonry-image-date">{image.date}</p>
                    <button className="masonry-view-button">
                      View Full Size
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlbumDetails;
